import { FC, useRef, MouseEvent } from "react";
import { useApp } from "../../../context/useApp";
import { _, parseNum} from "../../../utils/helpers";
import { Icon } from "../../../parts/Icon";
import { DEFAULT_DIALOG_HANDLE, SCREEN_BREAKPOINT_2, ROUTES, CHILDREN_PER_ADULT, BOOKING_STEPS_IN_ORDER, SURCHARGE_NAME, APPLY_SURCHARGE_ENABLED, SCREEN_BREAKPOINT_1 } from "../../../utils/symbols";
import { DialogHandle } from "../../../types";
import { ProductsBreakdown, ProductsBreakdownHandle } from "../../../parts/ProductsBreakdown";

export const Summary: FC = () => {

    const {
        state: { dialogHandles, booking, screen, products, tableMapHandle },
        actions: { navigate, genericError }
    } = useApp()

    const summarySliderRef = useRef<HTMLDivElement | null>(null)
    const summaryRef = useRef<HTMLDivElement | null>(null)
    const productsBreakdownRef = useRef<ProductsBreakdownHandle>(null)
    const dialogHandle:DialogHandle = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE
    const summary = booking.getBookingSummary()
    const showExpandableButton = booking.products.length > 1 || (booking.getTickets().length === 0 && booking.products.length > 0)
    const ariaLabel = `
        ${_('0ge_press__')}.
        ${booking.getStringSummary()}
    `

    function checkRequiredProducts() {
        if (booking.hasTableSelection() && booking.step !== "products") {
            return true
        }
        const requiredProduct = products.find(p => p.required && booking.getProductQty(p.id) < booking.pax)
        if (!requiredProduct) return true
        dialogHandle.openDialog({
            dialog: "required-product", 
            product: requiredProduct,
            toAdd: booking.pax - booking.getProductQty(requiredProduct.id),
        })
        return false
    }
    const handleContinue = (event: MouseEvent<HTMLButtonElement>) => {
        event.currentTarget.blur()

        // check for required products
        const requiredProductsOk = checkRequiredProducts()
        if (!requiredProductsOk) {
            return
        }

        if (!booking.hasTableSelection()) {
            booking.setBookingStep("personal-details").render()
            navigate(ROUTES.PERSONAL_DETAILS)
            return
        }

        if (booking.children > 0 && booking.getAdults() * CHILDREN_PER_ADULT < booking.children) {
            dialogHandle.openDialog({
                dialog: 'max-children', 
                className: 'max-children h-100 w-100 df aic jcc'
            }) 
            return
        }

        if (booking.step === 'tickets' && booking.isPossibleToReorganizeSeats()) {
            dialogHandle.openDialog({
                dialog: 'error',
                className: 'error w-100 h-100 df aic jcc',
                message: _('0ge_pleas__'),
                button: _('0ge_under__'),
                callback: () => dialogHandle.closeDialog(),
            }) 
            return
        }

        //remove & recalc
        if (booking.step === 'tickets' && APPLY_SURCHARGE_ENABLED) {

            const surcharge = products.find(product => product.name === SURCHARGE_NAME)
            if (!surcharge) {
                genericError(() => handleContinue(event))
                return
            }

            const computedSurcharge = booking.calcSurcharge(surcharge!)

            if (computedSurcharge.amount > 0) {
                dialogHandle.openDialog({
                    dialog: 'surcharge', 
                    surcharge: computedSurcharge,
                    className: 'surcharge h-100 w-100 df aic jcc'
                }) 
                return
            }
        }

        tableMapHandle.goToStartPosition()

        if (booking.step === 'products') {
            booking.setBookingStep('personal-details').render()
            navigate(ROUTES.PERSONAL_DETAILS)
        } else {
            booking.setBookingStep('products').render()
            navigate(ROUTES.TABLE_LAYOUT)
        }
        
    }

    const displayBlueSummary = (
        (booking.hasTableSelection() && screen.width > SCREEN_BREAKPOINT_2 )
    )
    const displayMobileSummary = (
        booking.hasTableSelection() || (
            !booking.hasTableSelection() && screen.width < SCREEN_BREAKPOINT_1
        )
    )

    return displayBlueSummary ? (
        <div ref={summaryRef} className="summary p-7 r3 p-a">
            <div className="df aifs" aria-hidden>
                <ProductsBreakdown 
                    ref={productsBreakdownRef}
                    productsSummary={summary.products} 
                    main={'ticket'}
                />
                {   showExpandableButton && (
                    <button 
                        aria-hidden
                        tabIndex={-1}
                        className="button b-0 fs-0 p-0 mt-2 ml-7 expand" 
                        onClick={(event) => {
                            productsBreakdownRef.current?.toggle()
                            event.currentTarget.classList.toggle('rt-180')
                        }}
                    >
                        <Icon icon="chevron"/>
                    </button>
                )}
            </div>

            <div className="df aic jcsb">
                <p className="df fd-c aifs w-100 mr-7" aria-hidden>
                    <span className="text s15-600 softer upper">{_('0ge_total__')}</span>
                    <span className="text s32-600 right">{parseNum('EUR', summary.total)}</span>
                </p>
                <button 
                    id="continue-button"
                    className="button black r3 w-100 px-10 py-7 text s20-500 white b-0" 
                    disabled={booking.pax === 0}
                    aria-hidden={booking.pax === 0}
                    onClick={handleContinue}
                    aria-label={ariaLabel}
                >
                    {_('0ge_conti__')}
                </button>
            </div>
        </div>
    ) : displayMobileSummary ? (
        <>
        <div 
            ref={summarySliderRef}
            className="w-100 p-a summary-slider p-8"
            aria-hidden
        >
            <ProductsBreakdown productsSummary={summary.products} mobile showAll/>
        </div>
        <div ref={summaryRef} className="summary p-0 w-100 df aic jcsb">
            <div className="w-100 px-7 df aic jcfe" aria-hidden>
                {summary.total > 0 && (
                    <button 
                        aria-hidden
                        tabIndex={-1}
                        onClick={(event) => {
                            summarySliderRef.current?.classList.toggle('opened')
                            const icon = event.currentTarget.querySelector('.chevron')
                            if (icon) icon.classList.toggle('rt-180')
                        }}
                        className="button black df aic px-0" 
                    >
                        <span className="text s16-600 white">{_('0ge_summa__')}</span>
                        <Icon icon={'chevron'} />
                    </button>
                )}
                <p className="text right white s20-600 w-100">{parseNum('EUR', summary.total)}</p>
            </div>
            <button 
                id="continue-button"
                className="button px-10 h-100 text s18-500 b-0 action-2 outline-shadow" 
                disabled={booking.getAdults() === 0}
                aria-hidden={booking.getAdults() === 0}
                aria-label={ariaLabel}
                onClick={handleContinue}
            >
                {_('0ge_conti__')}
            </button>
        </div>
        </>
    ) : null
}